/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/system/KioskInactivityWatcher.tsx
 *
 * Rôle :
 *   Composant **invisible** monté UNE SEULE FOIS dans AppLayout. Quand
 *   le mode kiosque est actif (cf. `useKioskMode()`), surveille
 *   l'inactivité utilisateur (mousedown / touchstart / keydown / wheel
 *   / scroll / pointermove) et redirige vers `/mon-espace` après
 *   `idleMinutes` minutes sans interaction.
 *
 *   Si `mode.enabled === false` (= pas en kiosque OU pas plastron),
 *   le composant ne pose **aucun listener** et ne fait rien, coût
 *   runtime nul.
 *
 *   **Pas de re-render** : tout est géré dans `useEffect` avec
 *   `useRef` pour le timer. Aucun state pour minimiser l'impact perf
 *   sur la borne (qui consulte beaucoup de pages).
 *
 *   **Pas de redirect intempestif** :
 *   - Si déjà sur `/mon-espace`, ne redirige pas (no-op).
 *   - Le timer se reset à CHAQUE interaction → si quelqu'un est en
 *     train de lire un atelier sans bouger, on attend `idleMinutes`
 *     pleines après son dernier geste pour redirect.
 *   - Test sandbox-safe : SSR-safe (vérif `typeof window`).
 *
 * Voir : useKioskMode hook, CLAUDE.md §5 entrée 2026-05-25 (borne
 *        plastron), §8.2 critère 5 (responsive, la borne sur
 *        écran géant doit gérer souris ET tactile).
 *
 * @author Mika
 * @since 2026-05-25
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKioskMode } from '@/hooks/useKioskMode';

/** Liste des événements considérés comme « activité utilisateur ». */
const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = [
  'mousedown',
  'touchstart',
  'keydown',
  'wheel',
  'scroll',
  'pointermove',
] as const;

/** Route cible quand l'inactivité expire. */
const HOME_ROUTE = '/mon-espace';

export function KioskInactivityWatcher(): null {
  const mode = useKioskMode();
  const navigate = useNavigate();
  const location = useLocation();

  // Ref vers le timer, évite les re-renders à chaque reset.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref vers la dernière route connue, sert à éviter le redirect si
  // déjà sur /mon-espace (no-op gracieux).
  const pathRef = useRef<string>(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    // Si le mode kiosque n'est pas actif, on ne pose AUCUN listener.
    // Coût runtime nul pour les utilisateurs normaux (la grande majorité).
    if (!mode.enabled) {
      return;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const idleMs = Math.max(60_000, mode.idleMinutes * 60_000); // floor 1 min

    const clearTimer = (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const triggerRedirect = (): void => {
      // Pas de redirect si déjà à l'accueil (no-op gracieux).
      if (pathRef.current === HOME_ROUTE) {
        // On re-arme pour la prochaine fenêtre d'inactivité.
        scheduleTimer();
        return;
      }
      navigate(HOME_ROUTE, { replace: true });
    };

    const scheduleTimer = (): void => {
      clearTimer();
      timerRef.current = setTimeout(triggerRedirect, idleMs);
    };

    const onActivity = (): void => {
      // Re-arme le timer à chaque interaction utilisateur.
      scheduleTimer();
    };

    // Pose des listeners avec `passive: true` (perf) sauf keydown
    // (qui n'accepte pas passive).
    for (const evt of ACTIVITY_EVENTS) {
      const opts: AddEventListenerOptions = {
        passive: evt !== 'keydown',
        capture: false,
      };
      document.addEventListener(evt, onActivity, opts);
    }

    // Démarrage initial du timer (= dès qu'on monte, on compte
    // l'inactivité).
    scheduleTimer();

    return () => {
      clearTimer();
      for (const evt of ACTIVITY_EVENTS) {
        document.removeEventListener(evt, onActivity);
      }
    };
  }, [mode.enabled, mode.idleMinutes, navigate]);

  return null;
}

// ──────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS / réutilisation :
//  - Composant 100 % générique. Pour NOLAS, juste changer la
//    constante HOME_ROUTE et la dépendance vers `useKioskMode`.
//  - Pattern listener pasif + ref + idempotence cleanup = pattern
//    safe React pour les watchers d'activité (zéro fuite mémoire).
// ──────────────────────────────────────────────────────────────────────────
